import React, { useState } from 'react';
import { toast } from 'sonner';
import s from './NovaApp.module.sass';
import { useAppContext } from '../../app-core/AppContext';
import NovaTicker from './NovaTicker';
import NovaFigmaSheet from './NovaFigmaSheet';
import { useNovaTheme } from './NovaThemeContext';
import { capturePageToClipboard } from './captureScreen';
import { Search, Check, Sun, Moon, ShieldCheck } from 'lucide-react';

// Путь к аватару: в конфиге хранится имя файла — как в оригинальном Header,
// резолвим через /data/avatar/ (абсолютные и http-пути не трогаем)
const resolveAvatarSrc = (avatar) => {
    if (!avatar) return null;
    return (avatar.startsWith('/') || avatar.startsWith('http'))
        ? avatar
        : `/data/avatar/${encodeURIComponent(avatar)}`;
};

// Ключи — те же, что в оригинальном Header (header.*)
const TITLES = {
    dashboard: ['header.dashboard', 'Дашборд'],
    trade: ['header.trade', 'Торговля'],
    wallet: ['header.wallet', 'Кошелек'],
    broker: ['header.broker', 'Брокер'],
    mailbox: ['header.mailbox', 'Почта'],
    settings: ['header.settings', 'Настройки'],
    'site-settings': ['siteSettings.title', 'Настройки сайта'],
};

export default function NovaHeader() {
    const {
        showExactMenu,
        nameUser,
        isVerificationActive,
        handleVerificationClick,
        toggleAdminPanel,
        siteSettings = {},
        safeT,
    } = useAppContext();

    const t = typeof safeT === 'function' ? safeT : (_k, f) => f;
    const { mode, toggleMode } = useNovaTheme();
    // В режиме отзыва мини-фигма не нужна: ни кнопки поиска, ни открытия по событию.
    const isOtzovik = siteSettings?.otzovikMode === true;
    // Минифигма — iOS-шит, открывается кнопкой поиска
    const [figmaOpen, setFigmaOpen] = useState(false);
    // Открытие из других мест (например, кнопкой в админке) — через событие
    React.useEffect(() => {
        if (isOtzovik) return undefined; // в отзыве не слушаем открытие
        const openFigma = () => setFigmaOpen(true);
        window.addEventListener('app:minifigma-open', openFigma);
        return () => window.removeEventListener('app:minifigma-open', openFigma);
    }, [isOtzovik]);
    const [titleKey, titleFallback] = TITLES[showExactMenu] || TITLES.dashboard;
    const isPremium = siteSettings.isPremium || siteSettings.premiumStatus;

    // Снимок всей страницы по клику на заголовок → в буфер обмена (1984×1116).
    const [capturing, setCapturing] = useState(false);
    const capturingRef = React.useRef(false);
    const handleTitleCapture = React.useCallback(async () => {
        if (capturingRef.current) return;
        capturingRef.current = true;
        setCapturing(true);
        const toastId = toast.loading(t('header.captureInProgress', 'Делаю скриншот…'));
        try {
            const root = document.querySelector('[data-nova-theme]');
            const res = await capturePageToClipboard(root, { section: showExactMenu });
            if (res.method === 'clipboard') {
                toast.success(t('header.captureCopied', 'Скриншот скопирован в буфер'), { id: toastId });
            } else {
                toast.success(t('header.captureSaved', 'Скриншот сохранён файлом'), { id: toastId });
            }
        } catch (err) {
            toast.error(t('header.captureFailed', 'Не удалось сделать скриншот'), { id: toastId });
        } finally {
            capturingRef.current = false;
            setCapturing(false);
        }
    }, [showExactMenu, t]);

    // Кнопка-камера в админке шлёт это событие — дублируем клик по заголовку.
    React.useEffect(() => {
        const onScreenshot = () => handleTitleCapture();
        window.addEventListener('app:screenshot', onScreenshot);
        return () => window.removeEventListener('app:screenshot', onScreenshot);
    }, [handleTitleCapture]);

    // Сравнение имён с trim — как в оригинальном Header
    const avatarMatch = nameUser && nameUser.trim()
        && nameUser.trim() === (siteSettings.defaultUserName || '').trim();
    const avatarSrc = avatarMatch ? resolveAvatarSrc(siteSettings.defaultUserAvatar) : null;

    return (
        <header className={s.header}>
            <div className={s.headerTitleWrap}>
                <h1
                    className={`${s.headerTitle} ${s.headerTitleClickable} ${capturing ? s.headerTitleBusy : ''}`}
                    onClick={handleTitleCapture}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTitleCapture(); } }}
                    title={t('header.captureHint', 'Скриншот страницы в буфер обмена (1984×1116)')}
                >
                    {t(titleKey, titleFallback)}
                </h1>
                {siteSettings.brokerLicenseNumber && (
                    <span className={s.licenseBadge}>
                        <ShieldCheck size={11} strokeWidth={2} aria-hidden="true" />
                        {t('header.brokerLicense', 'Лицензия Брокера №')} {siteSettings.brokerLicenseNumber}
                    </span>
                )}
            </div>

            {/* Бегущая строка курсов топ-30 монет (обновляется каждые 5 минут) */}
            <NovaTicker />

            <div className={s.headerActions}>
                {!isOtzovik && (
                    <>
                        <button
                            type="button"
                            className={s.iconBtn}
                            onClick={() => setFigmaOpen(true)}
                            aria-label={t('header.searchPlaceholder', 'Поиск')}
                        >
                            <Search size={16} strokeWidth={1.8} aria-hidden="true" />
                        </button>
                        <NovaFigmaSheet open={figmaOpen} onClose={() => setFigmaOpen(false)} />
                    </>
                )}

                {/* Переключатель светлой/тёмной темы */}
                <button
                    type="button"
                    className={s.iconBtn}
                    onClick={toggleMode}
                    aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
                >
                    {mode === 'dark'
                        ? <Sun size={16} strokeWidth={1.8} aria-hidden="true" />
                        : <Moon size={16} strokeWidth={1.8} aria-hidden="true" />}
                </button>

                {/* Контейнер с именем открывает админку; клик по аватарке — статус */}
                <button
                    type="button"
                    className={s.userChip}
                    onClick={toggleAdminPanel}
                >
                    <span
                        className={`${s.userAvatar} ${isVerificationActive ? s.userAvatarVerified : ''}`}
                        onClick={(e) => { e.stopPropagation(); handleVerificationClick && handleVerificationClick(); }}
                    >
                        {avatarSrc ? (
                            <img src={avatarSrc} alt="" />
                        ) : (
                            (nameUser || 'U').trim().charAt(0).toUpperCase()
                        )}
                        {isVerificationActive && (
                            <span className={s.verifiedBadge} aria-hidden="true">
                                <Check size={8} strokeWidth={3.5} />
                            </span>
                        )}
                    </span>
                    <span className={s.userMeta}>
                        <span className={s.userName}>{nameUser || '—'}</span>
                        <span className={s.userStatus}>
                            {isPremium ? 'Premium' : isVerificationActive
                                ? t('header.verified', 'Верифицирован')
                                : t('header.unverified', 'Не верифицирован')}
                        </span>
                    </span>
                </button>
            </div>
        </header>
    );
}
